/**
 * Pipeline hygiene: when a lead stops being someone's.
 *
 * The database does the actual clean-up every morning (gula_pipeline_hygiene,
 * migration 20261103090000_gula_pipeline_hygiene.sql). These functions only
 * show on the board what is about to happen, so the owner can act first.
 * Keep the thresholds in sync with that migration (and with
 * 20261103110000_gula_no_answer_free.sql: "No contesta" leads have no owner,
 * and go to "Reactivar" after 6 attempts, 3 calls on 2 slots plus WhatsApp on
 * 3 days, or a week after they came in).
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Days without activity before a lead goes back to the pool. */
export const RELEASE_QUIET_DAYS = 7;
/** Days past the recontact date (and without activity) before it is released. */
export const RECONTACT_GRACE_DAYS = 3;
/** Days after a lead came in before an unclosed talk goes to "Reactivar". */
export const LIMBO_DAYS = 30;
/** Days a "Sigo con él" extends the limbo deadline. */
export const KEEP_DAYS = 14;
/** No contesta: attempts that send a lead to "Reactivar". */
export const DEAD_ATTEMPTS = 6;
/** No contesta: days after it came in before it goes to "Reactivar" (4 nov: a week). */
export const NO_ANSWER_DAYS = 7;

/** Days before the release / limbo deadline when the card starts warning. */
const RELEASE_WARNING_DAYS = 2;
const LIMBO_WARNING_DAYS = 5;

const QUIET_STAGES = ["new", "no_answer", "first_contact", "second_contact"];
const LIMBO_STAGES = ["first_contact", "second_contact", "recontact"];

export interface HygieneLead {
  stage: string;
  sales_id?: unknown;
  created_at: string;
  stage_changed_at?: string | null;
  recontact_at?: string | null;
  call_attempts?: number | null;
  last_activity_at?: string | null;
  owner_since?: string | null;
  keep_until?: string | null;
}

export type LeadClock =
  | { kind: "release"; daysLeft: number }
  | { kind: "limbo"; daysLeft: number }
  | { kind: "last_attempt" }
  | { kind: "silent"; daysLeft: number };

const time = (value?: string | null): number =>
  value ? new Date(value).getTime() : Number.NEGATIVE_INFINITY;

/** Start of the day (local time) of a YYYY-MM-DD date. */
const dayStart = (date: string): number =>
  new Date(`${date.slice(0, 10)}T00:00:00`).getTime();

const startOfToday = (now: number): number => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return today.getTime();
};

/** Since when nobody has done anything with the lead (taking it restarts it). */
export const quietSince = (lead: HygieneLead): number => {
  const last = Math.max(time(lead.last_activity_at), time(lead.owner_since));
  return Number.isFinite(last)
    ? last
    : time(lead.stage_changed_at ?? lead.created_at);
};

/** The clean-up runs every morning at this time (Madrid). */
const RUN_HOUR = 6;
const RUN_MINUTE = 7;

/**
 * Calendar days from today until the morning run that acts on the deadline
 * (the first run at or after it): 0 = this morning, 1 = tomorrow…
 */
export const daysUntilRun = (deadline: number, now: number): number => {
  const run = new Date(Math.max(deadline, now));
  const atRunTime = new Date(run);
  atRunTime.setHours(RUN_HOUR, RUN_MINUTE, 0, 0);
  if (atRunTime.getTime() < run.getTime()) {
    atRunTime.setDate(atRunTime.getDate() + 1);
  }
  atRunTime.setHours(0, 0, 0, 0);
  return Math.round((atRunTime.getTime() - startOfToday(now)) / DAY_MS);
};

const hasFutureRecontact = (lead: HygieneLead, now: number): boolean =>
  lead.stage === "recontact" &&
  !!lead.recontact_at &&
  dayStart(lead.recontact_at) >= startOfToday(now);

/** When an owned lead goes back to the pool, or null if it never does. */
export const releaseDeadline = (
  lead: HygieneLead,
  now: number,
): number | null => {
  if (lead.sales_id == null) return null;
  const quiet = quietSince(lead);
  if (QUIET_STAGES.includes(lead.stage)) {
    return quiet + RELEASE_QUIET_DAYS * DAY_MS;
  }
  if (lead.stage === "recontact" && lead.recontact_at) {
    if (hasFutureRecontact(lead, now)) return null;
    return Math.max(
      dayStart(lead.recontact_at) + (RECONTACT_GRACE_DAYS + 1) * DAY_MS,
      quiet + RECONTACT_GRACE_DAYS * DAY_MS,
    );
  }
  return null;
};

/** When an unclosed talk goes to "Reactivar", or null if it does not apply. */
export const limboDeadline = (
  lead: HygieneLead,
  now: number,
): number | null => {
  if (!LIMBO_STAGES.includes(lead.stage) || hasFutureRecontact(lead, now)) {
    return null;
  }
  return Math.max(
    time(lead.created_at) + LIMBO_DAYS * DAY_MS,
    time(lead.keep_until),
  );
};

/**
 * What the card should warn about, the most urgent first: the lead goes to
 * "Reactivar" soon, or back to the pool soon. Null when there is nothing close.
 */
export const leadClock = (
  lead: HygieneLead,
  now: number = Date.now(),
): LeadClock | null => {
  if (
    lead.stage === "no_answer" &&
    (lead.call_attempts ?? 0) >= DEAD_ATTEMPTS - 1
  ) {
    return { kind: "last_attempt" };
  }
  if (lead.stage === "no_answer") {
    const silent = time(lead.created_at) + NO_ANSWER_DAYS * DAY_MS;
    if (silent - now <= LIMBO_WARNING_DAYS * DAY_MS) {
      return { kind: "silent", daysLeft: daysUntilRun(silent, now) };
    }
  }
  const limbo = limboDeadline(lead, now);
  if (limbo != null && limbo - now <= LIMBO_WARNING_DAYS * DAY_MS) {
    return { kind: "limbo", daysLeft: daysUntilRun(limbo, now) };
  }
  const release = releaseDeadline(lead, now);
  if (release != null && release - now <= RELEASE_WARNING_DAYS * DAY_MS) {
    return { kind: "release", daysLeft: daysUntilRun(release, now) };
  }
  return null;
};

/** Card text of a clock. */
export const clockLabel = (clock: LeadClock): string => {
  if (clock.kind === "last_attempt") {
    return "Último intento: si no contesta pasa a Reactivar";
  }
  const when =
    clock.daysLeft <= 0
      ? "hoy a primera hora"
      : clock.daysLeft === 1
        ? "mañana a primera hora"
        : `en ${clock.daysLeft} días`;
  if (clock.kind === "silent") return `Sin contestar: pasa a Reactivar ${when}`;
  return clock.kind === "limbo"
    ? `Sin cerrar: pasa a Reactivar ${when}`
    : `Sin moverlo: se libera ${when}`;
};
